import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import axios, { AxiosError } from "axios";
import React, { useEffect } from "react";
import { AuthHelper } from "../utils/api/AuthHelper";

export type FetchData<T> = {
  data: T | undefined;
  error: string | undefined;
  isLoading: boolean;
};

type methods = "head" | "options" | "put" | "post" | "patch" | "delete" | "get";

interface ApiCallProps {
  url: string | string[];
  refresh?: boolean;
  requestType?: methods;
  payload?: any;
  dependencies?: any[];
  poll?: number; //in seconds
  showLoaderOnFirstCallOnly?: boolean;
  showLoaderOnDependencyChangeOnly?: boolean;
}

const validateUrl = (url: string | string[]): boolean => {
  if (
    (Array.isArray(url) && url.length === 0) ||
    (typeof url === "string" && url === "")
  ) {
    return false;
  }
  return true;
};

/* This is a custom hook that fetches data from an API with the given URL.
 It uses the MSAL instance to get the access token and then makes a GET request to the API with the token in the header.
 It returns the data, error, and loading states. */

export const useApiCall = <T = unknown>({
  url,
  refresh = true,
  requestType = "get",
  payload = "",
  dependencies = [],
  poll = 0,
  showLoaderOnFirstCallOnly = true,
  showLoaderOnDependencyChangeOnly = false,
}: ApiCallProps): FetchData<T> => {
  const msalInstance = useMsal().instance as PublicClientApplication;
  const [data, setData] = React.useState<T | undefined>();
  const [error, setError] = React.useState<string | undefined>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const hasLoadedOnceRef = React.useRef(false);
  const serializedDependencies = JSON.stringify(dependencies);
  const previousDependenciesRef = React.useRef(serializedDependencies);
  const fetchApiResults = async (
    controller: AbortController,
    payloadArg: any
  ) => {
    let result;

    const accessToken = await AuthHelper.getAccessToken(msalInstance);
    if (accessToken === "") {
      throw new Error(String("Access token cannot be empty!"));
    }

    const headers = {
      "x-api-key": process.env.REACT_APP_APIM_KEY,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json;charset=UTF-8",
    };

    const axiosConfig = {
      signal: controller.signal,
      headers,
    };

    if (Array.isArray(url)) {
      const requests = url.map((url) =>
        axios[requestType](url, {
          ...axiosConfig,
        })
      );
      result = (await Promise.all(requests)) as T;
    } else {
      const response =
        requestType === "get"
          ? await axios[requestType](url, {
              ...axiosConfig,
            })
          : await axios[requestType](url, JSON.stringify(payloadArg), {
              ...axiosConfig,
            });

      // Check if the response is successful
      if (response.status !== 200) {
        throw new Error(
          String(`Request failed with status code ${response.status}`)
        );
      }
      result = response.data as T;
    }

    return result;
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const validUrl = validateUrl(url);
    let intervalId: NodeJS.Timeout | undefined;
    const dependenciesChanged =
      previousDependenciesRef.current !== serializedDependencies;
    previousDependenciesRef.current = serializedDependencies;
    const shouldShowLoader =
      !hasLoadedOnceRef.current ||
      (!showLoaderOnFirstCallOnly &&
        (!showLoaderOnDependencyChangeOnly || dependenciesChanged));

    const apiCallHandler = async (showLoader: boolean) => {
      try {
        if (showLoader) {
          setIsLoading(true);
        }
        setError(undefined);
        const result = await fetchApiResults(controller, payload);
        if (isMounted) {
          setData(result);
          hasLoadedOnceRef.current = true;
        }
      } catch (err) {
        const errorResponse: AxiosError = err as AxiosError;
        //If API rquest is cancelled, do not set the Error
        if (!axios.isCancel(err)) {
          setError(errorResponse.message);
          setData(undefined);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Fetch the data if the refresh flag is true
    if (refresh && validUrl) {
      apiCallHandler(shouldShowLoader);
      if (poll !== 0) {
        intervalId = setInterval(() => apiCallHandler(false), poll * 1000);
      }
    }

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      controller.abort();
    };
  }, [
    refresh,
    serializedDependencies,
    JSON.stringify(payload),
    showLoaderOnFirstCallOnly,
    showLoaderOnDependencyChangeOnly,
  ]);

  // Return the data, error, and loading state
  return { isLoading, error, data };
};
